import { Component, OnChanges } from '@angular/core';
import * as debug from 'debug';
import { SocketService } from '@app/core/socket.service';
import { ReplyAbstractComponent } from '@app/shared/components/reply/reply-abstract.component';
import cardMap from '@app/games/shared/card-map';
import { cardValues } from '@app/_v2/games/baccarat/models/card-values.model';
import { BalanceHistoryService } from '@app/core/account/balance-history.service';
const MersenneTwister = require('mersennetwister');

const log = debug('edg:reply-baccarat');

@Component({
  selector: 'app-reply-baccarat',
  template: `
    <div class="reply-loader" *ngIf="_loading || !data">
      <i class="fal fa-spinner fa-spin" [hidden]="_error"></i>
      <i class="fal fa-exclamation-triangle" [hidden]="!_error"></i>
    </div>
    <div class="baccarat-game-content" *ngIf="!_loading && data">
      <div class="baccarat-card-canvas">
        <div class="deck-cards">
          <div class="baccarat-card-container cards-4">
            <div class="baccarat-card card-container" *ngFor="let card of deck; let index = index">
              <div class="card__styled_card" [ngStyle]="{ left: index * 2.5 + 'em' }">
                <div
                  class="game-card card__front"
                  style="transform: rotateY(0deg)"
                  [ngClass]="'__card_' + getCardName(card)"
                  [title]="card"
                ></div>
                <div class="game-card card__back" style="transform: rotateY(180deg)"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="baccarat-card-container player-hand" [ngClass]="'cards-' + getCardCount()">
          <div class="baccarat-card-count">{{ getPlayerValue(data) }}</div>
          <div>
            <div class="baccarat-card card-container" *ngFor="let card of data.gameData.playerCards; let index = index">
              <div class="card__styled_card">
                <div
                  class="game-card card__front"
                  style="transform: rotateY(0deg)"
                  [ngClass]="'__card_' + getCardName(card)"
                  [title]="card"
                ></div>
                <div class="game-card card__back" style="transform: rotateY(180deg)"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="card-spacer"></div>

        <div class="baccarat-card-container" [ngClass]="'cards-' + getDealerCardCount()">
          <div class="baccarat-card-count">{{ getDealerValue(data) }}</div>
          <div>
            <div class="baccarat-card card-container" *ngFor="let card of data.gameData.dealerCards">
              <div class="card__styled_card">
                <div
                  class="game-card card__front"
                  style="transform: rotateY(0deg)"
                  [ngClass]="'__card_' + getCardName(card)"
                  [title]="card"
                ></div>
                <div class="game-card card__back" style="transform: rotateY(180deg)"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./reply-baccarat.component.scss'],
})
export class ReplyBaccaratComponent extends ReplyAbstractComponent implements OnChanges {
  data: any = null;
  deck: Array<number> = [];

  constructor(protected socket: SocketService, protected balanceHistory: BalanceHistoryService) {
    super(socket, balanceHistory);
  }

  protected initialize(data: any) {
    if (data.game !== 'baccarat') {
      this._error = true;
      return;
    }
    this.data = data;
    this.prepareDeck(this.data.gameData.numDealedCards);
  }

  protected generateGame() {
    const deck = this.reconstructDeck(this.serverSeed, this.playerSeed);
    const data = {
      gameData: {
        playerCards: [deck[0], deck[2]],
        dealerCards: [deck[1], deck[3]],
        shuffledDeck: deck,
        generated: true,
        numDealedCards: 4,
      },
      game: 'baccarat',
    };

    const playerValue = this.getPlayerValue(data);
    const dealerValue = this.getDealerValue(data);

    if (playerValue === 8 || playerValue === 9 || dealerValue === 8 || dealerValue === 9) {
      // If either the Player or Banker have 8 or 9 points, it is referred to as a "natural."
      // If there is at least one natural, then both hands will stand.
    } else if (playerValue <= 5) {
      // Otherwise, if the Player's total is 5 or less, then the Player hand will draw one more card,
      // otherwise, with 6 or 7 points, the Player hand stands.
      const card = deck[data.gameData.numDealedCards];
      data.gameData.numDealedCards++;
      data.gameData.playerCards.push(card);

      // If the Player does draw a third card, then use the Banker will use his positional advantage to decide
      // whether to take a third card according to his total and the third card drawn to the Player,
      // according to the following table.
      // @link https://wizardofodds.com/games/baccarat/basics/
      if (this.shouldBankerDrawAfterThirdCard(dealerValue, this.getCardValue(card))) {
        const card_ = deck[data.gameData.numDealedCards];
        data.gameData.numDealedCards++;
        data.gameData.dealerCards.push(card_);
      }
    } else if (dealerValue <= 5) {
      // If the Player hand stands with 6 or 7 points, then the Banker hand will draw a third card on
      // a total of 5 or less. Otherwise, with 6 or 7 points, the Banker will stand.
      const card = deck[data.gameData.numDealedCards];
      data.gameData.numDealedCards++;
      data.gameData.dealerCards.push(card);
    }

    this.initialize(data);
  }

  shouldBankerDrawAfterThirdCard(score: number, card: number) {
    return (
      [0, 1, 2].includes(score) ||
      (score === 3 && card !== 8) ||
      (score === 4 && ![0, 1, 8, 9].includes(card)) ||
      (score === 5 && [4, 5, 6, 7].includes(card)) ||
      (score === 6 && [6, 7].includes(card))
    );
  }

  prepareDeck(startIndex: number) {
    this.deck = [];
    const deck = this.data.gameData.shuffledDeck;
    if (deck.length > 0) {
      for (let i = startIndex; i < startIndex + 4; i++) {
        this.deck.push(deck[i]);
      }
    }
    this._loading = false;
  }

  getCardName(card: number): string {
    return cardMap[card % 52];
  }

  getDealerValue(game: any) {
    return this.getHandValue(game.gameData.dealerCards);
  }

  getPlayerValue(game: any) {
    return this.getHandValue(game.gameData.playerCards);
  }

  getHandValue(cards: Array<number>) {
    let sum = 0;
    cards.forEach(card => {
      sum += cardValues[card % 13];
    });

    if (sum > 9) {
      const stringed = String(sum);
      sum = Number(stringed.substring(1, stringed.length));
    }

    return sum;
  }

  getDealerCardCount(handIndex: number): number {
    if (!this.data || !this.data.gameData.dealerCards) {
      return 0;
    }
    return this.data.gameData.dealerCards.length;
  }

  getCardValue(card: number): number {
    return cardValues[card % 13];
  }

  reconstructDeck(srvSeed: string, clientSeed: string) {
    const deck = [];
    for (let i = 0; i < 8 * 52; i++) {
      // 8 decks
      deck.push(i);
    }
    return this.shuffleDeck(deck, srvSeed, clientSeed);
  }

  getCardCount(): number {
    if (!this.data || !this.data.gameData.playerCards) {
      return 0;
    }
    return this.data.gameData.playerCards.length;
  }

  private shuffleDeck(deck: Array<number>, serverSeed: string, clientSeed: string) {
    serverSeed = serverSeed.replace('0x', '');
    clientSeed = clientSeed.replace('0x', '');

    const clSeedSplitted = clientSeed.match(/.{1,6}/g);
    const srvSeedSplitted = serverSeed.match(/.{1,6}/g);
    const seedsCombined = clSeedSplitted.concat(srvSeedSplitted).map(item => parseInt(item, 16));

    const mt = new MersenneTwister();
    mt.seedArray(seedsCombined);
    for (let i = 0; i < 800000; i++) {
      mt.real();
    }

    const shuffledDeck = deck.slice(0);
    for (let i = shuffledDeck.length - 1; i > 0; i -= 1) {
      const j = Math.floor(mt.real() * (i + 1));
      const temp = shuffledDeck[i];
      shuffledDeck[i] = shuffledDeck[j];
      shuffledDeck[j] = temp;
    }

    return shuffledDeck;
  }
}



// WEBPACK FOOTER //
// /var/lib/jenkins/workspace/Prod_web-client/src/app/shared/components/reply/reply-baccarat.component.ts